import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import api from '../services/api';

type SuggestionRow = {
  materialId: number;
  code?: string;
  name: string;
  currentStock: number;
  reorderPoint: number;
  suggestedQty: number;
  supplierId?: number;
  supplierName?: string;
  supplierLeadTimeDays?: number;
  reason?: string;
};

export const PurchaseSuggestionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<SuggestionRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await api.request<{ items: SuggestionRow[] }>({ method: 'GET', url: '/purchases/material-suggestions' });
      setItems(res.items || []);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudieron cargar las sugerencias');
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    const blob = await api.request<Blob>({ method: 'GET', url: '/purchases/suggestions/export.csv', responseType: 'blob' as any });
    const url = window.URL.createObjectURL(blob as any);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'purchase_suggestions.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Sugerencias de Compra</Typography>
          <Typography variant="body2" color="text.secondary">
            Materias primas con stock por debajo del punto de reposición.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={exportCsv}>Exportar CSV</Button>
          <Button variant="contained" onClick={() => navigate('/purchases')}>Ir a Compras</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Materia Prima</TableCell>
                  <TableCell align="center">Stock</TableCell>
                  <TableCell align="center">Reposición</TableCell>
                  <TableCell align="center">Sugerido</TableCell>
                  <TableCell>Proveedor</TableCell>
                  <TableCell align="center">Tiempo de entrega</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell align="right">Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.materialId} hover>
                    <TableCell>{it.code || '-'}</TableCell>
                    <TableCell>{it.name}</TableCell>
                    <TableCell align="center">{it.currentStock}</TableCell>
                    <TableCell align="center">{it.reorderPoint}</TableCell>
                    <TableCell align="center">{it.suggestedQty}</TableCell>
                    <TableCell>{it.supplierName || '-'}</TableCell>
                    <TableCell align="center">{it.supplierLeadTimeDays ?? '-'}</TableCell>
                    <TableCell>{it.reason || '-'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate('/purchases', { state: { productId: it.materialId, suggestedQty: it.suggestedQty, supplierId: it.supplierId } })}
                      >
                        Crear compra
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {items.length === 0 && !error && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No hay sugerencias en este momento.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PurchaseSuggestionsPage;
